import { useQuery } from "@tanstack/react-query";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import type { ShiftTimeAnchor } from "../downloads/dailyPrintout";
import { backendService } from "../services/backend.service";

/** Convert an `HH:MM[:SS]` time-of-day string to minutes-of-day. */
const timeToMinutes = (time: string): number => {
	const [hours, minutes] = time.split(":");
	return Number(hours) * 60 + Number(minutes);
};

/**
 * Fetch a department's historically popular shift windows (from `department_history_distribution`),
 * mapped to {@link ShiftTimeAnchor}s for use as daily-printout grouping anchors.
 *
 * The endpoint already returns the correct set (jobs vs role groups) filtered server-side by the
 * `JobManagementEnabled` flag, so the FE consumes it as-is. Only fetched when `enabled` (i.e. the
 * "align group times to popular times" setting is on) to avoid needless calls.
 */
export const useHistoryDistribution = (enabled: boolean) => {
	const {
		chosenDepartment: { department_id: departmentId },
	} = useHospitalData();
	return useQuery<ShiftTimeAnchor[]>({
		queryKey: ["historyDistribution", departmentId],
		queryFn: async () => {
			const response = await backendService.getHistoryDistribution(departmentId);
			return response.data.map((entry) => ({
				startMin: timeToMinutes(entry.start_time),
				endMin: timeToMinutes(entry.end_time),
				ratio: entry.ratio,
				roleGroup: entry.role_group,
				jobId: entry.job_id ?? null,
			}));
		},
		enabled,
	});
};
